/* ============================================================
   RefId Sessions (2-min TTL, strict expiry) + Cookie fallback
   ============================================================ */

const PREFIX = "CashQuin";
const CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
const TTL_MS = 120_000; // 2 minutes
const LS_KEY = "refSessionsV3"; // sessionId -> RefSession
const LS_KEY_INDEX = "refSessionIndexV3"; // billerId -> sessionId[]
const COOKIE_PREFIX = "refSess_"; // cookie key = refSess_<sessionId>

export type RefSession = {
  sessionId: string; // unique per FetchBill (even for same billerId)
  billerId: string;
  refId: string; // 35-char ID
  timeIST: string; // 2025-09-26T17:02:00+05:30
  createdAtUTC: string; // ISO
  expiresAtUTC: string; // ISO (created + 2 min)
};

type SessionMap = Record<string, RefSession>;
type SessionIndex = Record<string, string[]>; // billerId -> sessionIds[]

export type RefOk = {
  ok: true;
  sessionId: string;
  billerId: string;
  refId: string;
  timeIST: string;
  expiresAtUTC: string;
};
export type RefErr =
  | { ok: false; error: "SESSION_NOT_FOUND"; http: 404 }
  | { ok: false; error: "REFID_EXPIRED"; http: 408; expiresAtUTC?: string };
export type RefResult = RefOk | RefErr;

/* ---------------- Utilities ---------------- */

function randomAlnum(len: number) {
  let out = "";
  for (let i = 0; i < len; i++)
    out += CHARS[Math.floor(Math.random() * CHARS.length)];
  return out;
}
function dayOfYearUTC(d: Date): number {
  const start = Date.UTC(d.getUTCFullYear(), 0, 1);
  const diff =
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) - start;
  return Math.floor(diff / 86_400_000) + 1;
}
/** 35-char RefId: PREFIX(8) + rand19 + Y(1) + DDD(3) + HH(2) + mm(2) */
export function generateRefId(): string {
  const now = new Date(); // UTC
  const y = now.getUTCFullYear() % 10;
  const ddd = String(dayOfYearUTC(now)).padStart(3, "0");
  const HH = String(now.getUTCHours()).padStart(2, "0");
  const mm = String(now.getUTCMinutes()).padStart(2, "0");
  return `${PREFIX}${randomAlnum(19)}${y}${ddd}${HH}${mm}`;
}
export function validateRefId(refId: string): boolean {
  if (!refId || refId.length !== 35) return false;
  if (!/^[A-Za-z0-9]{27}\d{1}\d{3}\d{4}$/.test(refId)) return false;
  const ddd = parseInt(refId.substring(28, 31), 10);
  const HH = parseInt(refId.substring(31, 33), 10);
  const mm = parseInt(refId.substring(33, 35), 10);
  return ddd >= 1 && ddd <= 366 && HH >= 0 && HH <= 23 && mm >= 0 && mm <= 59;
}
/** IST string like 2025-09-26T17:02:00+05:30 (no DST) */
export function nowISTOffsetString(date = new Date()): string {
  const istMs = date.getTime() + 330 * 60 * 1000; // UTC+05:30
  const d = new Date(istMs);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(
    d.getUTCHours(),
  )}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}+05:30`;
}

/* -------------- storage helpers -------------- */
function loadMap(): SessionMap {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? (JSON.parse(raw) as SessionMap) : {};
  } catch {
    return {};
  }
}
function saveMap(m: SessionMap) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(m));
  } catch {
    return {};
  }
}
function loadIndex(): SessionIndex {
  try {
    const raw = localStorage.getItem(LS_KEY_INDEX);
    return raw ? (JSON.parse(raw) as SessionIndex) : {};
  } catch {
    return {};
  }
}
function saveIndex(x: SessionIndex) {
  try {
    localStorage.setItem(LS_KEY_INDEX, JSON.stringify(x));
  } catch {
    return {};
  }
}
function addToIndex(billerId: string, sessionId: string) {
  const idx = loadIndex();
  const arr = idx[billerId] ?? [];
  arr.push(sessionId);
  idx[billerId] = Array.from(new Set(arr)); // unique
  saveIndex(idx);
}
function removeFromIndex(billerId: string, sessionId: string) {
  const idx = loadIndex();
  const arr = (idx[billerId] ?? []).filter((s) => s !== sessionId);
  if (arr.length) idx[billerId] = arr;
  else delete idx[billerId];
  saveIndex(idx);
}

/* -------------- cookie helpers -------------- */
function setCookie(key: string, value: string, ttlMs: number) {
  const expires = new Date(Date.now() + ttlMs).toUTCString();
  document.cookie = `${key}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}
function getCookie(key: string): string | undefined {
  const k = key + "=";
  const found = document.cookie.split("; ").find((c) => c.startsWith(k));
  return found ? decodeURIComponent(found.substring(k.length)) : undefined;
}
function delCookie(key: string) {
  document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
}

/* -------------- core utils -------------- */
function isExpired(rec: RefSession, now = Date.now()): boolean {
  return now > new Date(rec.expiresAtUTC).getTime();
}
function newRecord(billerId: string): RefSession {
  const refId = generateRefId();
  const timeIST = nowISTOffsetString(new Date());
  const created = new Date();
  const expires = new Date(created.getTime() + TTL_MS);
  const sessionId = crypto?.randomUUID
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}_${randomAlnum(8)}`;
  return {
    sessionId,
    billerId,
    refId,
    timeIST,
    createdAtUTC: created.toISOString(),
    expiresAtUTC: expires.toISOString(),
  };
}
function persist(rec: RefSession) {
  // localStorage
  const map = loadMap();
  map[rec.sessionId] = rec;
  saveMap(map);
  addToIndex(rec.billerId, rec.sessionId);
  // cookie (2-min TTL)
  setCookie(COOKIE_PREFIX + rec.sessionId, JSON.stringify(rec), TTL_MS);
}
function read(sessionId: string): RefSession | undefined {
  // try LS
  const map = loadMap();
  if (map[sessionId]) return map[sessionId];
  // fallback cookie
  const raw = getCookie(COOKIE_PREFIX + sessionId);
  if (!raw) return undefined;
  try {
    const rec = JSON.parse(raw) as RefSession;
    // hydrate LS again (helps if user cleared LS but cookie remained)
    const m2 = loadMap();
    m2[sessionId] = rec;
    saveMap(m2);
    addToIndex(rec.billerId, rec.sessionId);
    return rec;
  } catch {
    return undefined;
  }
}
function purge(sessionId: string, billerId?: string) {
  const map = loadMap();
  if (map[sessionId]) {
    const bId = billerId ?? map[sessionId].billerId;
    delete map[sessionId];
    saveMap(map);
    if (bId) removeFromIndex(bId, sessionId);
  }
  delCookie(COOKIE_PREFIX + sessionId);
}

/* ================== PUBLIC API ================== */

/** CALL ON EVERY FetchBill. Returns a brand-new session & auto-stores (LS + 2-min cookie). */
export function createRefSession(billerId: string): RefOk {
  const rec = newRecord(billerId);
  persist(rec);
  return {
    ok: true,
    sessionId: rec.sessionId,
    billerId: rec.billerId,
    refId: rec.refId,
    timeIST: rec.timeIST,
    expiresAtUTC: rec.expiresAtUTC,
  };
}

/** Short alias if you prefer: same as createRefSession */
export const createRefId = createRefSession;

/**
 * Use in Validation/Payment with the sessionId you got from FetchBill.
 * Strict: if >2 min, returns 408 REFID_EXPIRED **and auto-deletes** from LS + cookie.
 */
export function getRefSession(sessionId: string): RefResult {
  const rec = read(sessionId);
  if (!rec) return { ok: false, error: "SESSION_NOT_FOUND", http: 404 };

  if (!validateRefId(rec.refId)) {
    purge(sessionId, rec.billerId);
    return { ok: false, error: "SESSION_NOT_FOUND", http: 404 };
  }
  if (isExpired(rec)) {
    purge(sessionId, rec.billerId); // auto-clean
    return {
      ok: false,
      error: "REFID_EXPIRED",
      http: 408,
      expiresAtUTC: rec.expiresAtUTC,
    };
  }
  return {
    ok: true,
    sessionId: rec.sessionId,
    billerId: rec.billerId,
    refId: rec.refId,
    timeIST: rec.timeIST,
    expiresAtUTC: rec.expiresAtUTC,
  };
}

/** Short alias if you prefer the name you mentioned */
export const getRefId = getRefSession;

/** Manual cleanup (kept as you asked) */
export function clearRefSession(sessionId: string) {
  const rec = read(sessionId);
  if (rec) purge(sessionId, rec.billerId);
}

/** List active (non-expired) sessionIds for a biller; auto-prunes expired ones. */
export function listActiveSessionsForBiller(billerId: string): string[] {
  const idx = loadIndex();
  const ids = idx[billerId] ?? [];
  const out: string[] = [];
  for (const id of ids) {
    const rec = read(id);
    if (rec && !isExpired(rec)) out.push(id);
    else if (rec) purge(id, billerId); // prune expired
  }
  return out;
}

/** Get all active sessions snapshot (useful for debugging / admin) */
export function snapshotActiveSessions(): RefOk[] {
  const map = loadMap();
  const out: RefOk[] = [];
  for (const rec of Object.values(map)) {
    if (!isExpired(rec)) {
      out.push({
        ok: true,
        sessionId: rec.sessionId,
        billerId: rec.billerId,
        refId: rec.refId,
        timeIST: rec.timeIST,
        expiresAtUTC: rec.expiresAtUTC,
      });
    } else {
      purge(rec.sessionId, rec.billerId);
    }
  }
  return out;
}

/* ---- internal: pick latest-active (or latest-expired) for a biller ---- */
function findLatestForBiller(billerId: string): {
  active?: RefSession; // newest non-expired
  newestExpired?: RefSession; // newest among expired (if all expired)
} {
  const idx = loadIndex();
  const ids = idx[billerId] ?? [];
  let latestActive: RefSession | undefined;
  let latestExpired: RefSession | undefined;

  for (const sessionId of ids) {
    const rec = read(sessionId); // reads from LS or cookie
    if (!rec) continue;

    const createdTs = new Date(rec.createdAtUTC).getTime();
    if (!isExpired(rec)) {
      if (
        !latestActive ||
        createdTs > new Date(latestActive.createdAtUTC).getTime()
      ) {
        latestActive = rec;
      }
    } else {
      // prune expired as we see them
      purge(rec.sessionId, rec.billerId);
      if (
        !latestExpired ||
        createdTs > new Date(latestExpired.createdAtUTC).getTime()
      ) {
        latestExpired = rec;
      }
    }
  }

  return { active: latestActive, newestExpired: latestExpired };
}

/**
 * Get refId/time via BILLER ID (no sessionId).
 * Strategy: pick the MOST-RECENT non-expired session for that biller.
 * - If found -> ok:true with {refId,timeIST,sessionId,...}
 * - If none active but some existed and are expired -> 408 REFID_EXPIRED
 * - If none existed -> 404 SESSION_NOT_FOUND
 */
export function getRefByBillerId(billerId: string): RefResult {
  const picked = findLatestForBiller(billerId);

  if (picked.active) {
    const s = picked.active;
    return {
      ok: true,
      sessionId: s.sessionId,
      billerId: s.billerId,
      refId: s.refId,
      timeIST: s.timeIST,
      expiresAtUTC: s.expiresAtUTC,
    };
  }

  if (picked.newestExpired) {
    // newest one bhi expire ho chuka — caller ko timeout dikhao (redirect)
    return {
      ok: false,
      error: "REFID_EXPIRED",
      http: 408,
      expiresAtUTC: picked.newestExpired.expiresAtUTC,
    };
  }

  return { ok: false, error: "SESSION_NOT_FOUND", http: 404 };
}

/**
 * Get ALL active sessions for a biller (auto-prunes expired).
 * Useful when parallel transactions chal rahe ho and you want to show a list.
 */
export function getAllActiveForBiller(billerId: string): RefOk[] {
  const idx = loadIndex();
  const ids = idx[billerId] ?? [];
  const out: RefOk[] = [];
  for (const id of ids) {
    const rec = read(id);
    if (rec && !isExpired(rec)) {
      out.push({
        ok: true,
        sessionId: rec.sessionId,
        billerId: rec.billerId,
        refId: rec.refId,
        timeIST: rec.timeIST,
        expiresAtUTC: rec.expiresAtUTC,
      });
    } else if (rec) {
      purge(id, billerId);
    }
  }
  // newest-first
  out.sort(
    (a, b) =>
      new Date(b.expiresAtUTC).getTime() - new Date(a.expiresAtUTC).getTime(),
  );
  return out;
}

/**
 * (Optional) Pick explicit session for a biller if you know partial key.
 * E.g., if tum UI me accountNo/custId ka map rakhte ho, waha se correct sessionId pass kar do.
 * Otherwise prefer getRefByBillerId() which picks latest-active.
 */
export function getRefForBillerOrSession(params: {
  billerId: string;
  sessionId?: string;
}): RefResult {
  if (params.sessionId) return getRefSession(params.sessionId);
  return getRefByBillerId(params.billerId);
}

// // RefId format (35 chars):
// // "CashQuin" (8) + 19 random [A-Za-z0-9] + YDDD (year%10 + day-of-year) + HHmm (UTC 24h)
// const PREFIX = "CashQuin";
// const CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

// function randomAlnum(len: number) {
//   let out = "";
//   for (let i = 0; i < len; i++) {
//     out += CHARS[Math.floor(Math.random() * CHARS.length)];
//   }
//   return out;
// }

// function dayOfYearUTC(d: Date): number {
//   const start = Date.UTC(d.getUTCFullYear(), 0, 1);
//   const diff = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) - start;
//   return Math.floor(diff / (24 * 60 * 60 * 1000)) + 1;
// }

// /** 35-char RefId */
// export function generateRefId(): string {
//   const now = new Date(); // UTC base
//   const rand19 = randomAlnum(19);

//   const y = now.getUTCFullYear() % 10;
//   const ddd = String(dayOfYearUTC(now)).padStart(3, "0"); // 001..366
//   const HH = String(now.getUTCHours()).padStart(2, "0");
//   const mm = String(now.getUTCMinutes()).padStart(2, "0");

//   return `${PREFIX}${rand19}${y}${ddd}${HH}${mm}`;
// }

// /** Validate 35-char format */
// export function validateRefId(refId: string): boolean {
//   if (!refId || refId.length !== 35) return false;
//   // 27 alnum (includes "CashQuin"+19 random) + 1 digit (Y) + 3 digits (DDD) + 4 digits (HHmm)
//   if (!/^[A-Za-z0-9]{27}\d{1}\d{3}\d{4}$/.test(refId)) return false;

//   const ddd = parseInt(refId.substring(28, 31), 10);
//   const HH = parseInt(refId.substring(31, 33), 10);
//   const mm = parseInt(refId.substring(33, 35), 10);

//   if (ddd < 1 || ddd > 366) return false;
//   if (HH < 0 || HH > 23) return false;
//   if (mm < 0 || mm > 59) return false;
//   return true;
// }

// /** IST timestamp like 2025-09-26T17:02:00+05:30 (for logging) */
// export function nowISTOffsetString(date = new Date()): string {
//   // assume browser tz is IST or use Intl for generic offset format
//   const pad = (n: number) => String(n).padStart(2, "0");
//   const tzMin = -date.getTimezoneOffset();
//   const sign = tzMin >= 0 ? "+" : "-";
//   const hh = pad(Math.floor(Math.abs(tzMin) / 60));
//   const mm = pad(Math.abs(tzMin) % 60);
//   return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}${sign}${hh}:${mm}`;
// }

// /* ----------------- Persistence Helpers ----------------- */

// const LS_KEY = "refIdMap"; // maps billerId -> refId

// export type RefIdMap = Record<string, string>;

// /** read mapping from localStorage */
// export function loadRefIdMap(): RefIdMap {
//   try {
//     const raw = localStorage.getItem(LS_KEY);
//     return raw ? (JSON.parse(raw) as RefIdMap) : {};
//   } catch {
//     return {};
//   }
// }

// /** write mapping to localStorage */
// export function saveRefIdMap(map: RefIdMap) {
//   try {
//     localStorage.setItem(LS_KEY, JSON.stringify(map));
//   } catch {}
// }

// /** set billerId -> refId */
// export function setRefIdForBiller(billerId: string, refId: string) {
//   const m = loadRefIdMap();
//   m[billerId] = refId;
//   saveRefIdMap(m);
// }

// /** get billerId -> refId (if exists) */
// export function getRefIdForBiller(billerId: string): string | undefined {
//   const m = loadRefIdMap();
//   return m[billerId];
// }

// /** ensure billerId has a refId; returns existing or new one (and saves it) */
// export function ensureRefIdForBiller(billerId: string): string {
//   // const existing = getRefIdForBiller(billerId);
//   // if (existing && validateRefId(existing)) return existing;
//   const fresh = generateRefId();
//   setRefIdForBiller(billerId, fresh);
//   return fresh;
// }

// /** OPTIONAL: cookie version (if you prefer cookie persistence) */
// export function setRefIdCookie(billerId: string, refId: string, days = 7) {
//   const key = `refId_${encodeURIComponent(billerId)}`;
//   const expires = new Date(Date.now() + days * 864e5).toUTCString();
//   document.cookie = `${key}=${encodeURIComponent(refId)}; expires=${expires}; path=/; SameSite=Lax`;
// }
// export function getRefIdCookie(billerId: string): string | undefined {
//   const key = `refId_${encodeURIComponent(billerId)}=`;
//   const found = document.cookie.split("; ").find((c) => c.startsWith(key));
//   return found ? decodeURIComponent(found.substring(key.length)) : undefined;
// }
